import { Extent } from '@arcgis/core/geometry';
import escapeHtml from 'escape-html';
import { calculateBounds } from '../on-extents-change';
import GoogleMapsAttributionSvg from './google-maps-attribution-svg';

export interface GoogleAttribution {
  getAttribution: (zoom: number, extent: Extent) => Promise<string>;
}

/**
 * GoogleAttributionBuilder is responsible for obtaining tile attribution
 * for each of the Google basemap tiles through the
 * [Viewport Information API](https://developers.google.com/maps/documentation/tile/2d-tiles-overview#viewport_info_request).
 */
const GoogleAttributionBuilder = (googleApiKey: string, sessionId: string) => {
  const logo = `<a target="_blank" rel="noreferrer" href="https://www.google.com/help/terms_maps/">
    ${GoogleMapsAttributionSvg}</a>`;

  const getAttribution = async (zoom: number, extent: Extent) => {
    if (zoom === undefined || zoom === null || !extent || !sessionId) {
      return logo;
    }
    const intZoom = Math.round(zoom);
    const bounds = calculateBounds(extent, intZoom);
    const north = bounds.getNorthEast().lat();
    const south = bounds.getSouthWest().lat();
    const east = bounds.getNorthEast().lng();
    const west = bounds.getSouthWest().lng();

    try {
      const attribution = await fetch(
        `https://tile.googleapis.com/tile/v1/viewport?session=${sessionId}&key=${googleApiKey}&zoom=${intZoom}&north=${north}&south=${south}&east=${east}&west=${west}`,
      );
      if (!attribution.ok) {
        // Non-2xx response, fallback to logo
        return logo;
      }
      const attributionJson = await attribution.json();
      const copyright = escapeHtml(attributionJson.copyright);
      return `${logo}<span title="${copyright}">${copyright}</span>`;
    } catch (error) {
      // Network or parsing error, fallback to logo
      return logo;
    }
  };

  return {
    getAttribution,
  };
};

export default GoogleAttributionBuilder;
